import { useSuspenseQueries } from '@tanstack/react-query';
import { createFileRoute } from '@tanstack/react-router';
import { useCallback, useMemo } from 'react';
import { z } from 'zod';

import { useTranslation } from '@apple/lib/i18next';
import { useBackOrNavigate } from '@apple/lib/tanstack-router/hooks/use-router-history';
import { DetailsLayout } from '@apple/ui/layouts/DetailsLayout';
import { NotFoundPage } from '@apple/ui/shell/pages/NotFoundPage';
import { NotAuthorizedError } from '@apple/utils/api/errors';

import { AnnouncementForm } from '@apple/features/announcements/components/AnnouncementForm';
import { useAnnouncementManagement } from '@apple/features/announcements/hooks/useAnnouncementManagement';
import type { Announcement } from '@apple/features/announcements/models/management';
import { announcementQueries } from '@apple/features/announcements/queries/management';
import { requireAuth } from '@apple/features/auth/utils/require-auth';

export const Route = createFileRoute('/_authed/_admin/content/announcements_/$announcementId')({
	component: ManageAnnouncementRoute,
	errorComponent: ({ error }) => {
		if (error instanceof NotAuthorizedError) {
			return <NotFoundPage />;
		} else {
			throw error;
		}
	},
	parseParams: ({ announcementId }) => ({
		announcementId: z.string().parse(announcementId),
	}),
	beforeLoad: ({ params: { announcementId }, ...args }) => {
		requireAuth(args, {
			requirePermission: 'Manzanita.Security.Features.ContentManagement',
		});

		return {
			announcementQueryOptions: announcementQueries.getAnnouncementById(announcementId),
			announcementsQueryOptions: announcementQueries.getAllAnnouncements(),
		};
	},
	loader: ({ context: { queryClient, announcementQueryOptions, announcementsQueryOptions } }) => {
		void queryClient.prefetchQuery(announcementQueryOptions);
		void queryClient.prefetchQuery(announcementsQueryOptions);
	},
	wrapInSuspense: true,
});

function ManageAnnouncementRoute() {
	const { t } = useTranslation('content');
	const { announcementQueryOptions, announcementsQueryOptions } = Route.useRouteContext();
	const [announcementQuery, announcementsQuery] = useSuspenseQueries({
		queries: [announcementQueryOptions, announcementsQueryOptions],
	});
	const { edit, remove } = useAnnouncementManagement();

	const goBackToList = useBackOrNavigate();

	const handleEdit = useCallback(
		async (announcement: Announcement) => {
			await edit.mutateAsync(announcement, {
				onSuccess: async () => {
					await goBackToList('/content/announcements');
				},
			});
		},
		[edit, goBackToList],
	);

	const handleRemove = useCallback(
		async (announcementId: string) => {
			await remove.mutateAsync(announcementId, {
				onSuccess: async () => {
					await goBackToList('/content/announcements');
				},
			});
		},
		[goBackToList, remove],
	);

	const activeAllowed = useMemo(() => {
		const activeAnnouncement = announcementsQuery.data.find(x => x.active);

		if (!activeAnnouncement) {
			return true;
		}

		return activeAnnouncement.contentGroupId === announcementQuery.data.contentGroupId;
	}, [announcementQuery.data.contentGroupId, announcementsQuery.data]);

	return (
		<DetailsLayout title={t('announcements.edit.title')}>
			<AnnouncementForm
				announcement={announcementQuery.data}
				isEdit
				activeAllowed={activeAllowed}
				loading={edit.isPending || remove.isPending}
				submitted={edit.isSuccess || remove.isSuccess}
				onSave={handleEdit}
				onDelete={handleRemove}
			/>
		</DetailsLayout>
	);
}
